/**
 * @page /portal/forecasts
 * @description Kunden-Forecast Self-Service mit 3-Schritt Mini-Wizard.
 *   Schritt 1: Pakete Gesamt eingeben
 *   Schritt 2: Wochentags-Gewichtung (Presets + Slider)
 *   Schritt 3: Bestätigung mit Tagesverteilung + Einreichen
 *
 * Die Verteilungs-Logik ist identisch zum internen Forecast-Wizard (Forecast.jsx:1973-2054).
 *
 * @api GET  /api/portal/customer-forecasts
 * @api GET  /api/portal/customer-forecasts/:year/:month
 * @api PUT  /api/portal/customer-forecasts/:year/:month
 * @api POST /api/portal/customer-forecasts/:year/:month/submit
 * @api GET  /api/feiertage?jahr=YYYY
 * @roles kunde
 * @i18n portalForecasts, portalForecast
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Target } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader } from '../components/ui';
import ConfirmDialog from '../components/ConfirmDialog';

const API = import.meta.env.VITE_API_URL || '';
const apiGet = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());

function apiFetch(path, opts) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts })
    .then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    });
}

function StatusBadge({ status, t }) {
  const cfg = {
    missing: { bg: 'bg-gray-500/10', text: 'text-[var(--text-tertiary)]', label: t('portalForecasts.statusMissing') },
    draft: { bg: 'bg-gray-500/10', text: 'text-[var(--text-secondary)]', label: t('portalForecasts.statusDraft') },
    submitted: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: t('portalForecasts.statusSubmitted') },
    in_review: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: t('portalForecasts.statusInReview') },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', label: t('portalForecasts.statusApproved') },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: t('portalForecasts.statusRejected') },
  };
  const c = cfg[status] || cfg.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function fmt(n) {
  return n == null ? '\u2013' : Number(n).toLocaleString('de-DE');
}

// ── Presets — identisch zum internen Wizard (Forecast.jsx:1797-1804) ──
const presets = {
  standard: { labelKey: 'forecast.wizard.preset.standard', values: { montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 0.8, samstag: 0.8, sonntag: 1.1 } },
  equal: { labelKey: 'forecast.wizard.preset.equal', values: { montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 1.0, samstag: 0.5, sonntag: 0.3 } },
  monday: { labelKey: 'forecast.wizard.preset.monday_heavy', values: { montag: 1.5, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.8, freitag: 0.6, samstag: 0.3, sonntag: 0.2 } },
  friday: { labelKey: 'forecast.wizard.preset.friday_light', values: { montag: 1.1, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.9, freitag: 0.5, samstag: 0.2, sonntag: 0.1 } },
  midweek: { labelKey: 'forecast.wizard.preset.midweek_heavy', values: { montag: 0.9, dienstag: 1.1, mittwoch: 1.2, donnerstag: 1.1, freitag: 0.7, samstag: 0.3, sonntag: 0.2 } },
  noWeekend: { labelKey: 'forecast.wizard.preset.no_weekend', values: { montag: 1.2, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.9, freitag: 0.7, samstag: 0.0, sonntag: 0.0 } },
};

const weekdayKeys = ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'];
const weekdayLabelKeys = {
  montag: 'common.weekdays_short.montag', dienstag: 'common.weekdays_short.dienstag',
  mittwoch: 'common.weekdays_short.mittwoch', donnerstag: 'common.weekdays_short.donnerstag',
  freitag: 'common.weekdays_short.freitag', samstag: 'common.weekdays_short.samstag',
  sonntag: 'common.weekdays_short.sonntag',
};

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Versandlast-Verteilung — identisch zum internen Wizard (Forecast.jsx:1973-2054) ──
// Vereinfacht: kein cutoff_stunde (fix 14:00), kein SOP, kein FTE
function calculateDistribution(totalPackets, weights, year, month, feiertagSet) {
  if (!totalPackets || totalPackets <= 0) return { days: [], arbeitstage: 0, feiertageImMonat: 0 };

  const dim = new Date(year, month, 0).getDate();
  const jsDayToKey = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  // Fixed cutoff at 14:00 for portal (simplified)
  const cutoffStunde = 14;
  const vorCutoff = cutoffStunde / 24;
  const nachCutoff = 1 - vorCutoff;

  const allDays = [];
  let feiertageImMonat = 0;
  for (let d = 1; d <= dim; d++) {
    const date = new Date(year, month - 1, d);
    const jsDay = date.getDay();
    const isWeekend = jsDay === 0 || jsDay === 6;
    const isFeiertag = feiertagSet.has(isoDate(date));
    const istArbeitstag = !isWeekend && !isFeiertag;
    const dayKey = jsDayToKey[jsDay];
    const importGewicht = weights[dayKey] ?? 0;
    if (isFeiertag && !isWeekend) feiertageImMonat++;
    allDays.push({ d, date, jsDay, isWeekend, isFeiertag, istArbeitstag, dayKey, importGewicht, dayName: dayNames[jsDay] });
  }

  // Versandlast pro Arbeitstag (identisch zum internen Wizard)
  const dailyPkts = new Array(dim).fill(0);
  let arbeitstage = 0;

  for (let i = 0; i < allDays.length; i++) {
    const day = allDays[i];
    if (!day.istArbeitstag) continue;
    arbeitstage++;

    let versandlast = day.importGewicht * vorCutoff;

    for (let j = i - 1; j >= 0; j--) {
      const prev = allDays[j];
      if (!prev.istArbeitstag) {
        versandlast += prev.importGewicht;
      } else {
        versandlast += prev.importGewicht * nachCutoff;
        break;
      }
    }

    // Erster Arbeitstag: Vormonat-Freitag approximieren
    if (i === allDays.findIndex(dd => dd.istArbeitstag)) {
      let foundPrev = false;
      for (let j = i - 1; j >= 0; j--) {
        if (allDays[j].istArbeitstag) { foundPrev = true; break; }
      }
      if (!foundPrev && i > 0) {
        versandlast += (weights.freitag ?? 0.7) * nachCutoff;
      }
    }

    dailyPkts[i] = versandlast;
  }

  // Normalisieren
  const totalVersandlast = dailyPkts.reduce((s, v) => s + v, 0);
  const p = totalPackets;
  for (let i = 0; i < dim; i++) {
    if (totalVersandlast > 0 && dailyPkts[i] > 0) {
      dailyPkts[i] = Math.round(p * dailyPkts[i] / totalVersandlast);
    }
  }

  // Rundungskorrektur
  const sumDist = dailyPkts.reduce((s, v) => s + v, 0);
  const diff = p - sumDist;
  if (diff !== 0) {
    for (let i = dim - 1; i >= 0; i--) {
      if (dailyPkts[i] > 0) { dailyPkts[i] += diff; break; }
    }
  }

  // Peak-Tag finden
  const maxPkt = Math.max(...dailyPkts, 1);

  const days = allDays.map((day, i) => ({
    num: day.d,
    dateStr: `${String(day.d).padStart(2, '0')}.${String(month).padStart(2, '0')}.`,
    dayName: day.dayName,
    isWeekend: day.isWeekend,
    isFeiertag: day.isFeiertag,
    istArbeitstag: day.istArbeitstag,
    weight: day.importGewicht,
    packets: dailyPkts[i],
    isPeak: dailyPkts[i] === maxPkt && maxPkt > 0,
    isMontag: day.jsDay === 1,
  }));

  return { days, arbeitstage, feiertageImMonat, maxPkt };
}

// ── Versandlast-Preview Bar Chart (CSS-basiert, wie interner Wizard) ──
function ShippingPreviewChart({ days, maxPkt, t }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">
          {t('portalForecast.shippingPreview')}
        </div>
        <div className="text-[10px] text-[var(--text-tertiary)] font-mono">
          {t('portalForecast.maxPerDay', { n: fmt(maxPkt) })}
        </div>
      </div>
      <div className="flex items-end gap-[2px]" style={{ height: 90 }}>
        {days.map((d, i) => {
          const h = d.packets > 0 ? Math.max((d.packets / maxPkt) * 100, 4) : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end" style={{ height: '100%' }}>
              <div
                className={`w-full rounded-t transition-all duration-200 ${
                  d.isFeiertag ? 'bg-red-500/30' : d.isWeekend ? 'bg-surface-inset' : d.isMontag ? 'bg-[#338edc]/60' : 'bg-[#338edc]/40'
                }`}
                style={{ height: `${h}%` }}
                title={`${d.dateStr} ${d.dayName}: ${d.packets} ${t('portalForecast.packages')}`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 mt-3 text-[9px] text-[var(--text-tertiary)]">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-[#338edc]/60" />{t('common.weekdays_short.montag')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-[#338edc]/40" />{t('portalForecast.workday')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-red-500/30" />{t('portalForecast.holiday')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-surface-inset border border-line" />{t('portalForecast.weekend')}</span>
      </div>
    </div>
  );
}

// ── Stepper ──
function WizardStepper({ step, t }) {
  const steps = [
    { num: 1, label: t('portalForecast.step1') },
    { num: 2, label: t('portalForecast.step2') },
    { num: 3, label: t('portalForecast.step3') },
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((s, i) => (
        <div key={s.num} className="flex items-center gap-2">
          {i > 0 && <div className={`w-8 h-0.5 ${step >= s.num ? 'bg-[#338edc]' : 'bg-[var(--border-card)]'}`} />}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
            step === s.num ? 'bg-[#338edc] text-white' :
            step > s.num ? 'bg-[#338edc]/20 text-[#338edc]' :
            'bg-surface-inset text-[var(--text-tertiary)]'
          }`}>{s.num}</div>
          <span className={`text-xs ${step >= s.num ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-tertiary)]'}`}>
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Forecast Accuracy Components ──
function RatingDot({ rating }) {
  const colors = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[rating] || 'bg-gray-300'}`} />;
}

function TrendBadge({ value }) {
  if (value == null) return <span className="text-xs text-[var(--text-secondary)]">–</span>;
  const isPos = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-mono font-medium ${isPos ? 'text-emerald-500' : 'text-red-500'}`}>
      {isPos ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

function SuggestionCard({ suggestion, alreadySubmitted, t, lang }) {
  if (!suggestion) return null;

  const monthNames = lang === 'de'
    ? ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    : ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const targetLabel = `${monthNames[suggestion.target_month]} ${suggestion.target_year}`;

  return (
    <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
          <svg width="20" height="20" fill="none" stroke="#338edc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M3 3v18h18" /><path d="M7 16l4-4 4 4 5-8" />
          </svg>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-content">{t('portal.stats.forecastSuggestion')}</h3>
          <p className="text-xs text-[var(--text-secondary)]">{targetLabel}</p>
        </div>
      </div>

      {alreadySubmitted ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs text-emerald-700 dark:text-emerald-300">{t('portal.stats.forecastAlreadySubmitted')}</span>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('portal.stats.suggested')}</div>
            <div className="text-lg font-semibold font-mono text-content">{suggestion.suggested_total.toLocaleString('de-DE')}</div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('portal.stats.avg3m')}</div>
            <div className="text-lg font-semibold font-mono text-content">{suggestion.avg_3m.toLocaleString('de-DE')}</div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('portal.stats.trendLabel')}</div>
            <div className="text-lg font-semibold font-mono"><TrendBadge value={suggestion.trend_pct} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════
export default function PortalForecasts() {
  const { t, language } = useLanguage();
  const lang = language || 'en';
  const [searchParams, setSearchParams] = useSearchParams();
  const [forecasts, setForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const autoOpenDone = useRef(false);

  // Forecast accuracy + AI suggestion
  const [history, setHistory] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  // Change request state
  const [changeRequests, setChangeRequests] = useState([]);
  const [crModalOpen, setCrModalOpen] = useState(false);
  const [crNewValue, setCrNewValue] = useState('');
  const [crReason, setCrReason] = useState('');
  const [crSubmitting, setCrSubmitting] = useState(false);

  // Wizard state
  const [portalAktionen, setPortalAktionen] = useState([]);
  const [aktionModal, setAktionModal] = useState(null); // null | { mode: 'create'|'edit', item? }
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [step, setStep] = useState(1);
  const [totalPackets, setTotalPackets] = useState(0);
  const [weights, setWeights] = useState({ ...presets.standard.values });
  const [activePreset, setActivePreset] = useState('standard');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feiertage, setFeiertage] = useState([]);

  const loadForecasts = useCallback(() => {
    setLoading(true);
    apiFetch('/api/portal/customer-forecasts')
      .then(d => setForecasts(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch('/api/portal/aktionszeitraeume')
      .then(d => setPortalAktionen(Array.isArray(d) ? d : []))
      .catch(() => setPortalAktionen([]));
  }, []);

  useEffect(() => { loadForecasts(); }, [loadForecasts]);

  const loadChangeRequests = useCallback(() => {
    apiGet('/api/portal/customer-forecasts/change-requests').then(d => setChangeRequests(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Load forecast history + AI suggestion + change requests
  useEffect(() => {
    apiGet('/api/portal/forecast-history').then(d => setHistory(d)).catch(() => {});
    apiGet('/api/portal/stats/forecast-suggestion').then(d => setSuggestion(d)).catch(() => {});
    loadChangeRequests();
  }, []);

  // Auto-open forecast from ?open=YYYY-M query param (e.g. from dashboard banner)
  useEffect(() => {
    if (autoOpenDone.current || loading || forecasts.length === 0) return;
    const openParam = searchParams.get('open');
    if (!openParam) return;
    autoOpenDone.current = true;
    const [y, m] = openParam.split('-').map(Number);
    const match = forecasts.find(f => f.year === y && f.month === m);
    if (match && match.id) {
      openDetail(match);
      setSearchParams({}, { replace: true }); // clean URL
    }
  }, [loading, forecasts, searchParams]);

  // Load Feiertage when year changes
  useEffect(() => {
    if (!selected) return;
    fetch(`${API}/api/feiertage?jahr=${selected.year}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setFeiertage(Array.isArray(data) ? data : []))
      .catch(() => setFeiertage([]));
  }, [selected?.year]);

  const feiertagSet = useMemo(() => {
    const s = new Set();
    for (const f of feiertage) {
      const ds = typeof f.datum === 'string' ? f.datum.slice(0, 10) : new Date(f.datum).toLocaleDateString('sv-SE');
      s.add(ds);
    }
    return s;
  }, [feiertage]);

  const openDetail = async (fc) => {
    setSelected(fc);
    setDetailLoading(true);
    setDetail(null);
    setMsg('');
    setErr('');
    setStep(1);
    try {
      const d = await apiFetch(`/api/portal/customer-forecasts/${fc.year}/${fc.month}`);
      setDetail(d);
      // Restore total from existing daily_values if any
      const dv = d.forecast.daily_values || {};
      const existingTotal = Object.values(dv).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
      setTotalPackets(existingTotal || 0);
      setWeights({ ...presets.standard.values });
      setActivePreset('standard');
    } catch (e) {
      setErr(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setStep(1);
  };

  // Distribution calculation
  const distribution = useMemo(() => {
    if (!selected || totalPackets <= 0) return { days: [], arbeitstage: 0, feiertageImMonat: 0, maxPkt: 1 };
    return calculateDistribution(totalPackets, weights, selected.year, selected.month, feiertagSet);
  }, [selected, totalPackets, weights, feiertagSet]);

  const avgPerDay = distribution.arbeitstage > 0 ? Math.round(totalPackets / distribution.arbeitstage) : 0;

  // Build daily_values map for API
  function getDailyValues() {
    const values = {};
    distribution.days.forEach(d => { values[String(d.num)] = d.packets; });
    return values;
  }

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      await apiFetch(`/api/portal/customer-forecasts/${selected.year}/${selected.month}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_values: getDailyValues() }),
      });
      setMsg(t('portalForecasts.saved'));
      loadForecasts();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setMsg('');
    setErr('');
    try {
      // Save first, then submit
      await apiFetch(`/api/portal/customer-forecasts/${selected.year}/${selected.month}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_values: getDailyValues() }),
      });
      const result = await apiFetch(`/api/portal/customer-forecasts/${selected.year}/${selected.month}/submit`, {
        method: 'POST',
      });
      setDetail(prev => ({ ...prev, forecast: result }));
      setMsg(t('portalForecasts.submitted'));
      loadForecasts();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeRequest = async () => {
    if (!selected || !crNewValue) return;
    setCrSubmitting(true);
    setMsg('');
    setErr('');
    try {
      await apiFetch(`/api/portal/customer-forecasts/${selected.year}/${selected.month}/change-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_value: parseInt(crNewValue), reason: crReason }),
      });
      setCrModalOpen(false);
      setCrNewValue('');
      setCrReason('');
      setMsg(t('forecast.changeRequest.pending'));
      loadChangeRequests();
    } catch (e) {
      setErr(e.message);
    } finally {
      setCrSubmitting(false);
    }
  };

  const canEdit = detail && ['draft', 'rejected'].includes(detail.forecast.status);
  const isReadOnly = detail && !canEdit;

  // Find pending change request for current selection
  const pendingCR = selected && changeRequests.find(
    cr => cr.year === selected.year && cr.month === selected.month && cr.status === 'pending'
  );

  // Compute totalFc for read-only summary + grid (needs detail)
  const totalFc = (() => {
    if (!detail) return 0;
    const opSoll = detail.operative_soll || {};
    const dv = detail.forecast?.daily_values || {};
    const hasOp = Object.keys(opSoll).length > 0;
    if (detail.operative_total != null && detail.operative_total > 0) return detail.operative_total;
    return hasOp
      ? Object.values(opSoll).reduce((s, v) => s + (Number(v) || 0), 0)
      : Object.values(dv).reduce((s, v) => s + (parseInt(v) || 0), 0);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  // ── Read-only view for submitted/approved/in_review forecasts ──
  function renderReadOnlyGrid() {
    // Operative SOLL aus tages_forecasts, Fallback auf customer daily_values
    const opSoll = detail.operative_soll || {};
    const dv = detail.forecast.daily_values || {};
    const daysInMonth = new Date(selected.year, selected.month, 0).getDate();
    const hasOperative = Object.keys(opSoll).length > 0;
    // SOLL: operative tages_forecasts wenn vorhanden, sonst customer daily_values
    const getSoll = (day) => hasOperative ? (opSoll[day] || 0) : (parseInt(dv[String(day)]) || 0);
    // totalFc comes from component scope (computed above)
    // IST aus pack_stats (backend liefert jetzt pack_stats statt customer_forecast_actuals)
    let totalAct = null;
    if (detail.actuals) {
      totalAct = 0;
      Object.values(detail.actuals).forEach(v => { if (v != null) totalAct += Number(v); });
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 480 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <th style={{ padding: '10px 8px 10px 16px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'center' }}>{t('portalForecasts.day')}</th>
              <th style={{ padding: '10px 8px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>{t('portalForecasts.forecast')}</th>
              <th style={{ padding: '10px 8px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>{t('portalForecasts.actual')}</th>
              <th style={{ padding: '10px 8px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>{t('portalForecasts.delta')}</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const d = new Date(selected.year, selected.month - 1, day);
              const dow = d.getDay();
              const isWe = dow === 0 || dow === 6;
              const fc = getSoll(day);
              const act = detail.actuals?.[day];
              const hasAct = act != null;
              const delta = hasAct && fc > 0 ? Number(act) - fc : null;
              const ratio = hasAct && fc > 0 ? Number(act) / fc : null;
              let cellBg;
              if (ratio !== null && !isWe) {
                if (ratio >= 1.2) cellBg = 'rgba(34, 197, 94, 0.08)';
                else if (ratio < 0.8) cellBg = 'rgba(239, 68, 68, 0.08)';
              }
              // Check if this day falls in any Aktionszeitraum
              const dayStr = `${selected.year}-${String(selected.month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const dayAktionen = portalAktionen.filter(a => a.datum_von?.slice(0,10) <= dayStr && a.datum_bis?.slice(0,10) >= dayStr && !['abgeschlossen'].includes(a.status));
              const hasAktion = dayAktionen.length > 0 && !isWe;
              if (hasAktion && !cellBg) cellBg = 'rgba(245, 158, 11, 0.10)';
              return (
                <tr key={day} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 8px 8px 16px', fontSize: 13, fontWeight: 500, fontVariantNumeric: 'tabular-nums', textAlign: 'center', color: isWe ? 'var(--text-muted)' : 'var(--text-primary)', ...(isWe ? { backgroundColor: 'var(--bg-inset)' } : {}), ...(hasAktion ? { backgroundColor: 'rgba(245, 158, 11, 0.10)' } : {}) }}>
                    <div className="flex items-center justify-center gap-1">
                      {d.toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      {hasAktion && <span className={`text-[10px] px-1 py-0.5 rounded font-semibold whitespace-nowrap ${dayAktionen[0].status === 'genehmigt' || dayAktionen[0].status === 'aktiv' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/20 text-amber-700'}`} title={dayAktionen.map(a => `${a.titel} (${a.multiplikator ? a.multiplikator + 'x' : '+' + a.zusatz_orders}) – ${a.status === 'genehmigt' || a.status === 'aktiv' ? t('aktionszeitraeume.statusGenehmigt') : t('aktionszeitraeume.statusEingereicht')}`).join(', ')}>⚡ {dayAktionen[0].titel} {dayAktionen[0].multiplikator ? dayAktionen[0].multiplikator + 'x' : ''}</span>}
                    </div>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(isWe ? { backgroundColor: 'var(--bg-inset)' } : {}), ...(hasAktion && !isWe ? { backgroundColor: 'rgba(245, 158, 11, 0.10)' } : {}) }}>
                    <span className="font-mono text-content" style={{ fontSize: 14, fontWeight: 700 }}>{fc > 0 ? fmt(fc) : isWe ? '\u2013' : '0'}</span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(isWe ? { backgroundColor: 'var(--bg-inset)' } : {}), ...(cellBg ? { backgroundColor: cellBg } : {}), ...(hasAktion && !cellBg && !isWe ? { backgroundColor: 'rgba(245, 158, 11, 0.10)' } : {}) }}>
                    {hasAct ? <span className="font-mono" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{fmt(act)}</span> : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{'\u2013'}</span>}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...(isWe ? { backgroundColor: 'var(--bg-inset)' } : {}), ...(hasAktion && !isWe ? { backgroundColor: 'rgba(245, 158, 11, 0.10)' } : {}) }}>
                    {delta !== null ? <span className="font-mono" style={{ fontSize: 12, fontWeight: 600, color: ratio >= 0.9 ? '#10B981' : ratio >= 0.7 ? '#F59E0B' : '#EF4444' }}>{delta > 0 ? '+' : ''}{fmt(delta)}</span> : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{'\u2013'}</span>}
                  </td>
                </tr>
              );
            })}
            <tr style={{ borderTop: '2px solid var(--border-primary)', borderBottom: '2px solid var(--border-primary)' }}>
              <td style={{ padding: '10px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#338edc', backgroundColor: 'var(--bg-inset)' }}>
                Σ {t('portalForecasts.total')}
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right', backgroundColor: 'var(--bg-inset)' }}>
                <span className="font-mono text-content" style={{ fontSize: 14, fontWeight: 700 }}>{fmt(totalFc)}</span>
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right', backgroundColor: 'var(--bg-inset)' }}>
                <span className="font-mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{totalAct != null ? fmt(totalAct) : '\u2013'}</span>
              </td>
              <td style={{ padding: '10px 8px', textAlign: 'right', backgroundColor: 'var(--bg-inset)' }}>
                {totalAct != null && totalFc > 0 ? (
                  <span className="font-mono" style={{ fontSize: 13, fontWeight: 700, color: totalAct >= totalFc * 0.9 ? '#10B981' : totalAct >= totalFc * 0.7 ? '#F59E0B' : '#EF4444' }}>
                    {totalAct - totalFc > 0 ? '+' : ''}{fmt(totalAct - totalFc)}
                  </span>
                ) : <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{'\u2013'}</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('portalForecasts.title')} subtitle={t('portalForecasts.subtitle')} />

      {/* ── Accuracy KPI ── */}
      {history && history.months && history.months.length > 0 && (
        <div className="bg-surface-card border border-line rounded-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Target size={16} className="text-[#22D3EE]" />
                <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">
                  {t('portal.forecastHistory.avgAccuracy')}
                </div>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-[var(--text-primary)]">
                  {history.average_accuracy != null ? `${history.average_accuracy}%` : '\u2013'}
                </span>
                {history.trend && history.trend !== 'stable' && (
                  <span className={`text-sm font-medium ${history.trend === 'improving' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {history.trend === 'improving' ? '\u2191' : '\u2193'} {t(`portal.forecastHistory.${history.trend}`)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5"><RatingDot rating="green" /> ≤ 10%</span>
              <span className="flex items-center gap-1.5"><RatingDot rating="yellow" /> 10–20%</span>
              <span className="flex items-center gap-1.5"><RatingDot rating="red" /> &gt; 20%</span>
            </div>
          </div>
          <div className="mt-3 h-2 bg-surface-inset rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                history.average_accuracy >= 90 ? 'bg-emerald-500' :
                history.average_accuracy >= 80 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(history.average_accuracy || 0, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* ── AI Forecast Suggestion ── */}
      {suggestion?.suggestion && (
        <SuggestionCard
          suggestion={suggestion.suggestion}
          alreadySubmitted={suggestion.already_submitted}
          t={t}
          lang={lang}
        />
      )}

      {/* ── Aktionszeiträume ── */}
      <div className="bg-surface-card border border-line rounded-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider">
            {t('forecastPromo.title')}
          </div>
          <button
            onClick={() => setAktionModal({ mode: 'create' })}
            className="text-xs font-medium px-3 py-1.5 rounded-lg text-white"
            style={{ backgroundColor: '#338edc' }}
          >
            + {t('forecastPromo.newPromo')}
          </button>
        </div>
        {portalAktionen.length === 0 ? (
          <div className="text-sm text-[var(--text-tertiary)] text-center py-3">{t('forecastPromo.noEntries')}</div>
        ) : (
          <div className="space-y-2">
            {portalAktionen.map(a => {
              const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };
              const c = typColors[a.typ] || '#6B7280';
              return (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-line/50">
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: c }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text-primary)]">{a.titel}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {new Date(a.datum_von).toLocaleDateString('de-AT')} – {new Date(a.datum_bis).toLocaleDateString('de-AT')}
                      {' · '}{a.multiplikator ? `${a.multiplikator}x` : `+${fmt(a.zusatz_orders)}`}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: c + '20', color: c }}>
                    {t(`forecastPromo.typ.${a.typ}`)}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    a.status === 'aktiv' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                    a.status === 'genehmigt' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                    a.status === 'abgelehnt' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                    a.status === 'abgeschlossen' ? 'bg-gray-500/10 text-[var(--text-tertiary)]' :
                    'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                  }`}>
                    {t(`forecastPromo.status.${a.status}`)}
                  </span>
                  {a.status === 'abgelehnt' && a.ablehnungs_grund && (
                    <div className="text-[10px] text-red-500 truncate max-w-[120px]" title={a.ablehnungs_grund}>
                      {a.ablehnungs_grund}
                    </div>
                  )}
                  {['eingereicht', 'abgelehnt'].includes(a.status) && (
                    <div className="flex gap-1">
                      <button onClick={() => setAktionModal({ mode: 'edit', item: a, _useZusatz: !!a.zusatz_orders })} className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                        {t('forecastPromo.editPromo').split(' ')[0]}
                      </button>
                      <button
                        onClick={() => setConfirmDialog({
                          title: t('confirm.loeschen.titel'),
                          message: t('confirm.loeschen.text'),
                          variant: 'danger',
                          confirmLabel: t('confirm.loeschen'),
                          cancelLabel: t('confirm.abbrechen'),
                          onConfirm: async () => {
                            setConfirmDialog(null);
                            await apiFetch(`/api/portal/aktionszeitraeume/${a.id}`, { method: 'DELETE' });
                            loadForecasts();
                          },
                        })}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        {t('forecastPromo.delete')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Portal Aktionszeitraum Modal */}
      {aktionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAktionModal(null)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-xl w-full max-w-md p-6 border border-[var(--border-card,#E5E7EB)]" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4 text-[var(--text-primary)]">
              {aktionModal.mode === 'edit' ? t('forecastPromo.editPromo') : t('forecastPromo.newPromo')}
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const useMulti = fd.get('value_mode') === 'multiplikator';
              const body = {
                titel: fd.get('titel'),
                typ: fd.get('typ'),
                datum_von: fd.get('datum_von'),
                datum_bis: fd.get('datum_bis'),
                multiplikator: useMulti && fd.get('multiplikator') ? parseFloat(fd.get('multiplikator')) : null,
                zusatz_orders: !useMulti && fd.get('zusatz_orders') ? parseInt(fd.get('zusatz_orders')) : null,
                beschreibung: fd.get('beschreibung'),
              };
              if (!body.multiplikator && !body.zusatz_orders) body.multiplikator = 2;
              const url = aktionModal.mode === 'edit'
                ? `/api/portal/aktionszeitraeume/${aktionModal.item.id}`
                : '/api/portal/aktionszeitraeume';
              await apiFetch(url, { method: aktionModal.mode === 'edit' ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
              setAktionModal(null);
              loadForecasts();
            }} className="space-y-3">
              <input name="titel" defaultValue={aktionModal.item?.titel || ''} placeholder={t('forecastPromo.titleField')} required className="w-full border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]" />
              <select name="typ" defaultValue={aktionModal.item?.typ || 'sonstige'} className="w-full border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]">
                {['black_week','sale','launch','kampagne','saison','sonstige'].map(typ => (
                  <option key={typ} value={typ}>{t(`forecastPromo.typ.${typ}`)}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input name="datum_von" type="date" defaultValue={aktionModal.item?.datum_von?.slice(0,10) || ''} required className="border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]" />
                <input name="datum_bis" type="date" defaultValue={aktionModal.item?.datum_bis?.slice(0,10) || ''} required className="border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]" />
              </div>
              {/* Multiplikator / Zusatz-Orders Toggle */}
              <div>
                <div className="flex items-center gap-1 p-[3px] bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] border border-line mb-2">
                  <label className={`flex-1 text-center text-xs py-1.5 rounded-[8px] cursor-pointer transition-all ${
                    !(aktionModal._useZusatz) ? 'bg-white shadow-sm font-semibold text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
                  }`}>
                    <input type="radio" name="value_mode" value="multiplikator" defaultChecked={!aktionModal.item?.zusatz_orders} className="sr-only"
                      onChange={() => setAktionModal(m => ({ ...m, _useZusatz: false }))} />
                    {t('forecastPromo.multiplier')}
                  </label>
                  <label className={`flex-1 text-center text-xs py-1.5 rounded-[8px] cursor-pointer transition-all ${
                    aktionModal._useZusatz ? 'bg-white shadow-sm font-semibold text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'
                  }`}>
                    <input type="radio" name="value_mode" value="zusatz_orders" defaultChecked={!!aktionModal.item?.zusatz_orders} className="sr-only"
                      onChange={() => setAktionModal(m => ({ ...m, _useZusatz: true }))} />
                    {t('forecastPromo.additionalOrders')}
                  </label>
                </div>
                {!aktionModal._useZusatz ? (
                  <input name="multiplikator" type="number" step="0.1" min="1" defaultValue={aktionModal.item?.multiplikator || ''} placeholder={t('forecastPromo.multiplier') + ' (z.B. 3.0)'} className="w-full border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]" />
                ) : (
                  <input name="zusatz_orders" type="number" min="1" defaultValue={aktionModal.item?.zusatz_orders || ''} placeholder={t('forecastPromo.additionalOrders') + ' (z.B. 500)'} className="w-full border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)]" />
                )}
              </div>
              <textarea name="beschreibung" defaultValue={aktionModal.item?.beschreibung || ''} placeholder={t('forecastPromo.description')} rows={2} className="w-full border border-line rounded-lg px-3 py-2 text-sm bg-transparent text-[var(--text-primary)] resize-none" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setAktionModal(null)} className="px-4 py-2 rounded-lg text-sm border border-line text-[var(--text-secondary)]">{t('forecastPromo.cancel')}</button>
                <button type="submit" className="px-4 py-2 rounded-lg text-sm text-white" style={{ backgroundColor: '#338edc' }}>{t('forecastPromo.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Monats-Liste ── */}
      <div className="bg-surface-card border border-line rounded-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portalForecasts.month')}</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portalForecasts.totalForecast')}</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portalForecasts.totalActual')}</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portal.forecastHistory.deviation')}</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portalForecasts.deadline')}</th>
              <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('portalForecasts.status')}</th>
            </tr>
          </thead>
          <tbody>
            {forecasts.map(fc => {
              const isOverdue = fc.deadline && new Date(fc.deadline) < new Date() && ['draft', 'rejected'].includes(fc.status);
              const nowDate = new Date();
              const isCurrentMonth = fc.year === nowDate.getFullYear() && fc.month === nowDate.getMonth() + 1;
              const isPast = (fc.year * 12 + fc.month) < (nowDate.getFullYear() * 12 + nowDate.getMonth() + 1);
              // Find accuracy data from history
              const histMonth = history?.months?.find(m => m.month === `${fc.year}-${String(fc.month).padStart(2, '0')}`);
              const deviation = histMonth?.deviation;
              const devColor = histMonth?.rating === 'green'
                ? 'text-emerald-600 dark:text-emerald-400'
                : histMonth?.rating === 'yellow'
                  ? 'text-amber-600 dark:text-amber-400'
                  : histMonth?.rating === 'red'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-[var(--text-secondary)]';
              return (
                <tr key={fc.id || `${fc.year}-${fc.month}`} onClick={() => openDetail(fc)}
                  className={`border-b border-line hover:bg-surface-hover cursor-pointer transition-colors ${selected?.year === fc.year && selected?.month === fc.month ? 'bg-surface-hover' : ''}`}>
                  <td className="px-4 py-3 text-xs font-medium text-content">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{t(`common.months.${fc.month - 1}`)} {fc.year}</span>
                      {isCurrentMonth && <span className="text-[10px] text-[var(--text-secondary)]">{t('portal.forecasts.running')}</span>}
                      {fc.source === 'internal' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#F8F9FC] text-[#6B7280]">{t('portal.forecastByWeshipLabel')}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-content text-right font-mono">
                    {fc.source === 'none' ? (
                      <span className="text-[10px] font-semibold text-[#338edc]">{t('portalForecasts.enterForecast')}</span>
                    ) : fc.total_forecast > 0 ? fmt(fc.total_forecast) : '\u2013'}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-secondary)] text-right font-mono">
                    {Number(fc.total_actual) > 0 ? fmt(fc.total_actual) : '\u2013'}
                    {isCurrentMonth && Number(fc.total_actual) > 0 && <span className="ml-1 text-[9px] text-[var(--text-tertiary)]">{t('portal.forecasts.running')}</span>}
                  </td>
                  <td className={`px-4 py-3 text-xs text-right font-mono font-medium ${devColor}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {deviation != null ? `${deviation > 0 ? '+' : ''}${deviation.toFixed(1)}%` : '\u2013'}
                      {histMonth && <RatingDot rating={histMonth.rating} />}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-xs text-right ${isOverdue ? 'text-red-500 font-semibold' : 'text-[var(--text-secondary)]'}`}>
                    {fc.deadline ? new Date(fc.deadline).toLocaleDateString('de-AT') : '\u2013'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isOverdue
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400">{t('portal.forecasts.overdue')}</span>
                      : <StatusBadge status={fc.status} t={t} />
                    }
                  </td>
                </tr>
              );
            })}
            {forecasts.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-xs text-[var(--text-secondary)]">{t('portalForecasts.noForecasts')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Detail Loading ── */}
      {detailLoading && (
        <div className="bg-surface-card border border-line rounded-card p-8 flex justify-center">
          <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      )}

      {/* ── Detail ── */}
      {detail && selected && (
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-line">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-content">
                {t(`common.months.${selected.month - 1}`)} {selected.year}
              </h2>
              <StatusBadge status={detail.forecast.status} t={t} />
            </div>
            <button onClick={closeDetail} className="text-[var(--text-secondary)] hover:text-content text-xs">
              {t('portalForecasts.close')}
            </button>
          </div>

          {/* Messages */}
          {msg && <div className="mx-5 mt-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">{msg}</div>}
          {err && <div className="mx-5 mt-3 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-600 dark:text-red-400">{err}</div>}

          {/* Rejected comment */}
          {detail.forecast.status === 'rejected' && detail.forecast.review_comment && (
            <div className="mx-5 mt-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <div className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">{t('portalForecasts.rejectedReason')}</div>
              <div className="text-xs text-red-600 dark:text-red-400">{detail.forecast.review_comment}</div>
            </div>
          )}

          {/* ── Read-only view for non-editable forecasts ── */}
          {isReadOnly && (
            <div className="p-5">
              {/* Pending change request banner */}
              {pendingCR && (
                <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 flex items-start gap-3">
                  <span className="text-amber-500 mt-0.5 flex-shrink-0">&#9203;</span>
                  <div>
                    <div className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                      {t('forecast.changeRequest.pendingBanner', { old: fmt(pendingCR.old_value), new: fmt(pendingCR.new_value) })}
                    </div>
                    {pendingCR.reason && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
                        {t('forecast.changeRequest.reasonLabel', { reason: pendingCR.reason })}
                      </div>
                    )}
                    <div className="text-[10px] text-amber-500 mt-1">{t('forecast.changeRequest.info')}</div>
                  </div>
                </div>
              )}

              {/* Locked info */}
              <div className="mb-4 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {t('forecast.changeRequest.locked')}
              </div>

              {/* Summary */}
              <div className="flex items-center gap-6 mb-4">
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold tracking-wider">{t('portalForecasts.totalForecast')}</div>
                  <div className="text-lg font-bold font-mono text-content">{fmt(totalFc)}</div>
                  {detail.vorschlag_basis && detail.vorschlag_basis !== 'kunde' && (
                    <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{t('portal.forecasts.sourceInternal')}</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold tracking-wider">{t('portalForecasts.deadline')}</div>
                  <div className="text-sm text-content">{detail.forecast.deadline ? new Date(detail.forecast.deadline).toLocaleDateString('de-AT') : '\u2013'}</div>
                </div>
                <div className="ml-auto">
                  {!pendingCR && (
                    <button
                      onClick={() => { setCrNewValue(String(totalFc || '')); setCrReason(''); setCrModalOpen(true); }}
                      className="px-4 py-2 text-xs font-medium border border-line rounded-[10px] text-[var(--text-secondary)] hover:bg-surface-hover transition-colors"
                    >
                      {t('forecast.changeRequest.requestChange')}
                    </button>
                  )}
                </div>
              </div>
              {renderReadOnlyGrid()}
              {detail.forecast.status === 'submitted' && (
                <div className="mt-4 text-xs text-[var(--text-secondary)]">
                  {t('portalForecasts.submittedInfo', { date: detail.forecast.submitted_at ? new Date(detail.forecast.submitted_at).toLocaleDateString('de-AT') : '' })}
                </div>
              )}
              {detail.forecast.status === 'approved' && (
                <div className="mt-4 text-xs text-emerald-600 dark:text-emerald-400">
                  {t('portalForecasts.approvedInfo', { date: detail.forecast.approved_at ? new Date(detail.forecast.approved_at).toLocaleDateString('de-AT') : '' })}
                </div>
              )}
            </div>
          )}

          {/* ── Change Request Modal ── */}
          {crModalOpen && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCrModalOpen(false)}>
              <div className="bg-surface-card border border-line rounded-card p-6 w-full max-w-[480px] mx-4" onClick={e => e.stopPropagation()}>
                <h3 className="text-sm font-semibold text-content mb-4">{t('forecast.changeRequest.title')}</h3>

                <div className="mb-4">
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold tracking-wider mb-1">
                    {t('forecast.changeRequest.currentValue')}
                  </div>
                  <div className="text-lg font-bold font-mono text-content">{fmt(totalFc)} {t('portalForecasts.packages')}</div>
                </div>

                <div className="mb-4">
                  <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold tracking-wider block mb-1">
                    {t('forecast.changeRequest.newValue')}
                  </label>
                  <input
                    type="number" min="1"
                    value={crNewValue}
                    onChange={e => setCrNewValue(e.target.value)}
                    className="w-full p-3 text-lg font-bold text-center rounded-xl border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/10 outline-none"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  />
                </div>

                <div className="mb-4">
                  <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold tracking-wider block mb-1">
                    {t('forecast.changeRequest.reason')}
                  </label>
                  <textarea
                    value={crReason}
                    onChange={e => setCrReason(e.target.value)}
                    placeholder={t('forecast.changeRequest.reasonPlaceholder')}
                    rows={3}
                    className="w-full p-3 text-sm rounded-xl border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/10 outline-none resize-none"
                  />
                </div>

                <div className="text-[10px] text-[var(--text-tertiary)] mb-4 flex items-start gap-2">
                  <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {t('forecast.changeRequest.info')}
                </div>

                <div className="flex justify-end gap-2">
                  <button onClick={() => setCrModalOpen(false)} className="px-4 py-2 text-xs font-medium border border-line rounded-[10px] text-[var(--text-secondary)] hover:bg-surface-hover transition-colors">
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleChangeRequest}
                    disabled={crSubmitting || !crNewValue || parseInt(crNewValue) <= 0}
                    className="px-5 py-2 text-xs font-semibold text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
                  >
                    {crSubmitting ? '...' : t('forecast.changeRequest.submit')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Mini-Wizard for editable forecasts ── */}
          {canEdit && (
            <div className="p-5">
              <WizardStepper step={step} t={t} />

              {/* ── Step 1: Pakete Gesamt ── */}
              {step === 1 && (
                <div>
                  <div className="text-sm text-[var(--text-secondary)] mb-6">
                    {t(`common.months.${selected.month - 1}`)} {selected.year} — {distribution.arbeitstage} {t('portalForecast.workdays')}
                    {distribution.feiertageImMonat > 0 && (
                      <span> · {distribution.feiertageImMonat} {t('portalForecast.holiday')}{distribution.feiertageImMonat > 1 ? 'e' : ''}</span>
                    )}
                  </div>

                  <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-2">
                    <Target size={14} className="text-[#338edc]" />
                    {t('portalForecast.packetsTotal')}
                  </label>
                  <input
                    type="number" min="0"
                    value={totalPackets || ''}
                    onChange={e => setTotalPackets(parseInt(e.target.value) || 0)}
                    className="w-full p-4 text-3xl font-bold text-center rounded-xl border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/10 outline-none"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    placeholder={t('portalForecast.packetsPlaceholder')}
                  />

                  {detail.previous_month && detail.previous_month.total_forecast > 0 && (
                    <div className="mt-3 text-sm text-[var(--text-secondary)]">
                      {t('portalForecast.prevMonth')}: {fmt(detail.previous_month.total_forecast)} {t('portalForecast.packages')}
                      {totalPackets > 0 && (
                        <span className={`ml-2 font-medium ${
                          Math.abs(((totalPackets - detail.previous_month.total_forecast) / detail.previous_month.total_forecast) * 100) > 20
                            ? 'text-amber-500' : 'text-[var(--text-secondary)]'
                        }`}>
                          ({'\u0394'} {(((totalPackets - detail.previous_month.total_forecast) / detail.previous_month.total_forecast) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  )}

                  {totalPackets > 0 && distribution.arbeitstage > 0 && (
                    <div className="mt-4 p-3 bg-surface-inset rounded-lg text-sm text-[var(--text-secondary)]">
                      {'\u2248'} {fmt(avgPerDay)} {t('portalForecast.avgPerDay')}
                    </div>
                  )}

                  {/* Show internal forecast info if WeShip created one */}
                  {detail.vorschlag_basis && detail.vorschlag_basis !== 'kunde' && detail.operative_total > 0 && (
                    <div className="mt-4 p-3 border border-[var(--border-primary)] rounded-lg flex items-center gap-2">
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#F8F9FC] text-[#6B7280] shrink-0">{t('portal.forecastByWeshipLabel')}</span>
                      <span className="text-sm text-[var(--text-secondary)]">
                        {t('portal.forecastByWeship', { pakete: fmt(detail.operative_total) })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Step 2: Gewichtung ── */}
              {step === 2 && (
                <div>
                  <div className="text-sm text-[var(--text-secondary)] mb-4">
                    {t('portalForecast.weightingDesc')}
                  </div>

                  {/* Preset Buttons */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {Object.entries(presets).map(([key, p]) => (
                      <button key={key}
                        onClick={() => { setWeights({ ...p.values }); setActivePreset(key); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          activePreset === key
                            ? 'bg-surface-card border-[#338edc] text-[#338edc] font-semibold'
                            : 'bg-surface-inset border-line-hover text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-surface-active'
                        }`}>
                        {t(p.labelKey)}
                      </button>
                    ))}
                  </div>

                  {/* Weekday Sliders (Mo-Fr) */}
                  <div className="space-y-3 mb-4">
                    {['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag'].map(key => {
                      const pct = weights[key] / 2.0;
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <span className="w-8 text-xs font-bold text-[var(--text-tertiary)]">{t(weekdayLabelKeys[key])}</span>
                          <div className="flex-1">
                            <input type="range" min="0.1" max="2.0" step="0.1"
                              value={weights[key]}
                              onChange={e => { setWeights(w => ({ ...w, [key]: Number(e.target.value) })); setActivePreset(''); }}
                              className="w-full h-1.5 bg-surface-active rounded-full appearance-none cursor-pointer accent-[#338edc]"
                            />
                          </div>
                          <span className="w-10 text-right font-mono text-sm font-bold text-content">{weights[key].toFixed(1)}</span>
                          <div className="w-16 h-2 bg-surface-inset rounded-full overflow-hidden">
                            <div className="h-full bg-[#338edc]/50 rounded-full transition-all" style={{ width: `${pct * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Weekend Sliders (Sa-So) */}
                  <div className="border-t border-line-hover pt-3 mb-6">
                    <div className="text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">
                      {t('portalForecast.weekendImport')}
                    </div>
                    <div className="space-y-3">
                      {['samstag', 'sonntag'].map(key => {
                        const pct = weights[key] / 1.5;
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <span className="w-8 text-xs font-bold text-[var(--text-tertiary)]">{t(weekdayLabelKeys[key])}</span>
                            <div className="flex-1">
                              <input type="range" min="0.0" max="1.5" step="0.1"
                                value={weights[key]}
                                onChange={e => { setWeights(w => ({ ...w, [key]: Number(e.target.value) })); setActivePreset(''); }}
                                className="w-full h-1.5 bg-surface-active rounded-full appearance-none cursor-pointer accent-orange-400"
                              />
                            </div>
                            <span className="w-10 text-right font-mono text-sm font-bold text-content">{weights[key].toFixed(1)}</span>
                            <div className="w-16 h-2 bg-surface-inset rounded-full overflow-hidden">
                              <div className="h-full bg-orange-400/50 rounded-full transition-all" style={{ width: `${pct * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-[var(--text-tertiary)] mt-2">{t('portalForecast.weekendNote')}</div>
                  </div>

                  {/* Versandlast-Preview */}
                  {totalPackets > 0 && distribution.days.length > 0 && (
                    <ShippingPreviewChart days={distribution.days} maxPkt={distribution.maxPkt} t={t} />
                  )}
                </div>
              )}

              {/* ── Step 3: Bestätigung ── */}
              {step === 3 && (
                <div>
                  <div className="text-sm text-[var(--text-secondary)] mb-4">
                    {t('portalForecast.confirmDesc')}
                  </div>

                  {/* Summary Card */}
                  <div className="bg-surface-card border border-line rounded-card overflow-hidden mb-4">
                    <div className="grid grid-cols-2 divide-x divide-line">
                      <div className="p-4">
                        <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">{t('portalForecast.period')}</div>
                        <div className="text-sm font-semibold text-content">{t(`common.months.${selected.month - 1}`)} {selected.year}</div>
                      </div>
                      <div className="p-4">
                        <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">{t('portalForecast.packetsTotal')}</div>
                        <div className="text-sm font-bold text-[#338edc]">{fmt(totalPackets)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 divide-x divide-line border-t border-line">
                      <div className="p-4">
                        <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">{t('portalForecast.workdays')}</div>
                        <div className="text-sm font-semibold text-content">{distribution.arbeitstage}</div>
                      </div>
                      <div className="p-4">
                        <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">{t('portalForecast.avgPerDay')}</div>
                        <div className="text-sm font-semibold text-content">{fmt(avgPerDay)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Gewichtung summary */}
                  <div className="flex items-center gap-3 mb-4 text-xs text-[var(--text-secondary)] flex-wrap">
                    <span className="font-semibold">{t('portalForecast.weighting')}:</span>
                    {weekdayKeys.map(k => (
                      <span key={k}>{t(weekdayLabelKeys[k])} <strong className="text-[var(--text-primary)]">{weights[k]?.toFixed(1)}</strong></span>
                    ))}
                  </div>

                  {/* Daily Distribution Table */}
                  <div className="bg-surface-card border border-line rounded-card overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-line flex items-center justify-between">
                      <span className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider">{t('portalForecast.dailyDistribution')}</span>
                      <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{distribution.arbeitstage} {t('portalForecast.workdays')}</span>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-surface-card">
                          <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <th className="text-left px-4 py-2" style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('portalForecast.date')}</th>
                            <th className="text-left px-2 py-2" style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('portalForecast.day')}</th>
                            <th className="text-right px-2 py-2" style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('portalForecast.weight')}</th>
                            <th className="text-right px-4 py-2" style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('portalForecast.sollPackets')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {distribution.days.filter(d => d.istArbeitstag).map(d => (
                            <tr key={d.num} className="hover:bg-surface-hover transition-colors">
                              <td className="px-4 py-1.5 text-xs text-content" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.dateStr}</td>
                              <td className="px-2 py-1.5 text-xs text-[var(--text-secondary)]">
                                {d.dayName}
                                {d.isPeak && <span className="ml-1 text-[9px] font-bold text-[#338edc]">{t('portalForecast.peak')}</span>}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-right font-mono text-[var(--text-tertiary)]">{d.weight.toFixed(2)}</td>
                              <td className="px-4 py-1.5 text-xs text-right font-mono font-semibold text-content" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(d.packets)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t-2 border-[var(--border-primary)]">
                          <tr>
                            <td colSpan={3} className="px-4 py-2 text-xs font-bold text-[var(--text-tertiary)] uppercase">Σ {t('portalForecasts.total')}</td>
                            <td className="px-4 py-2 text-right font-mono text-xs font-extrabold text-[#338edc]">{fmt(totalPackets)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Navigation Buttons ── */}
              <div className="flex items-center justify-between mt-8">
                <div>
                  {step > 1 && (
                    <button onClick={() => setStep(step - 1)}
                      className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                      {'\u2190'} {t('portalForecast.back')}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {step === 3 && (
                    <button onClick={handleSave} disabled={saving}
                      className="px-4 py-2 text-xs font-medium border border-line rounded-[10px] text-[var(--text-secondary)] hover:bg-surface-hover disabled:opacity-50 transition-colors">
                      {saving ? t('common.saving') : t('portalForecasts.saveDraft')}
                    </button>
                  )}
                  {step < 3 ? (
                    <button
                      onClick={() => setStep(step + 1)}
                      disabled={step === 1 && totalPackets <= 0}
                      className="px-5 py-2 text-xs font-semibold text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
                      {t('portalForecast.next')} {'\u2192'}
                    </button>
                  ) : (
                    <button onClick={handleSubmit} disabled={submitting || totalPackets <= 0}
                      className="px-5 py-2 text-xs font-semibold text-white bg-[#338edc] rounded-[10px] hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
                      {submitting ? t('portalForecast.submitting') : t('portalForecast.submit')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Forecast Accuracy Info ── */}
      {history && history.months && history.months.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            {t('portal.forecastHistory.hint')}
          </p>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        variant={confirmDialog?.variant}
        confirmLabel={confirmDialog?.confirmLabel}
        cancelLabel={confirmDialog?.cancelLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
